import React, { useMemo } from "react";
import { formatDateVN } from "../../../utils/formatUtils";
import {
  STATUS_MAP,
  buildSignatureSlots,
  SignatureBlock,
  formatAmountInWords,
} from "../advance/AdvanceDocumentPaper";

const DocRow = ({ label, value, mono }) => (
  <div className="flex gap-2">
    <span className="shrink-0 text-slate-500 min-w-[148px]">- {label}:</span>
    <span className={`font-medium text-slate-800 break-words ${mono ? "font-mono" : ""}`}>
      {value || "…………………………"}
    </span>
  </div>
);

/** Giấy phiếu đề nghị thanh toán */
const PaymentDocumentPaper = ({ data, lines = [] }) => {
  const status = STATUS_MAP[data?.status]?.label || data?.status;
  const code = data?.request_code || data?.payment_code;
  const signatureSlots = useMemo(() => buildSignatureSlots(data), [data]);
  const totalAmount = Number(data?.payment_amount || 0);

  return (
    <article className="bg-white text-slate-900 shadow-[0_12px_40px_rgba(15,23,42,0.12)] border border-slate-200/80 relative overflow-hidden print:shadow-none">
      <div className="absolute inset-y-0 left-0 w-1.5 bg-red-700" />
      <div className="px-6 py-7 md:px-8 md:py-9 space-y-5">
        <header className="text-center space-y-1 border-b border-slate-300 pb-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">
            Cộng hòa xã hội chủ nghĩa Việt Nam
          </p>
          <p className="text-[11px] font-semibold italic text-slate-500">
            Độc lập – Tự do – Hạnh phúc
          </p>
          <div className="mx-auto w-16 border-t border-slate-400 mt-2" />
          <h1 className="pt-3 text-lg md:text-xl font-black uppercase tracking-wide text-slate-900">
            Đề nghị thanh toán
          </h1>
          <p className="text-xs text-slate-500">
            Số: <span className="font-bold text-slate-800">{code || "—"}</span>
          </p>
        </header>

        <section className="space-y-2 text-[13px] leading-relaxed">
          <DocRow label="Họ và tên" value={data?.employee_name} />
          <DocRow
            label="Đơn vị"
            value={[data?.branch_name, data?.department_name].filter(Boolean).join(" — ")}
          />
          <DocRow
            label="Ngày chứng từ"
            value={formatDateVN(data?.payment_date || data?.created_at)}
          />
          {data?.advance_request_id ? (
            <DocRow
              label="Đối chiếu tạm ứng"
              value={`${data.settlement_label || "Khớp tạm ứng"}${
                data.advance_amount != null
                  ? ` · Tạm ứng ${Number(data.advance_amount || 0).toLocaleString("vi-VN")} ₫`
                  : ""
              }`}
            />
          ) : null}
          {(data?.bank_name || data?.account_number) && (
            <>
              <DocRow label="Ngân hàng" value={data.bank_name} />
              <DocRow label="Số tài khoản" value={data.account_number} mono />
              <DocRow label="Chủ tài khoản" value={data.account_holder} />
            </>
          )}
        </section>

        <section>
          <p className="text-[12px] font-bold uppercase tracking-wide text-slate-700 mb-2">
            Nội dung thanh toán
          </p>
          <table className="w-full border border-slate-300 text-[12px]">
            <thead>
              <tr className="bg-slate-50">
                <th className="border border-slate-300 px-2 py-1.5 w-10 text-center font-bold">
                  STT
                </th>
                <th className="border border-slate-300 px-2 py-1.5 text-left font-bold">
                  Lý do / Nội dung
                </th>
                <th className="border border-slate-300 px-2 py-1.5 w-[28%] text-right font-bold">
                  Số tiền (₫)
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="border border-slate-300 px-2 py-3 text-center text-slate-400 italic"
                  >
                    {data?.payment_reason || "Chưa có nội dung"}
                  </td>
                </tr>
              ) : (
                lines.map((line, idx) => (
                  <tr key={line.id || idx}>
                    <td className="border border-slate-300 px-2 py-1.5 text-center align-top">
                      {line.line_no || idx + 1}
                    </td>
                    <td className="border border-slate-300 px-2 py-1.5 align-top">
                      {line.description || "—"}
                    </td>
                    <td className="border border-slate-300 px-2 py-1.5 text-right font-semibold tabular-nums align-top">
                      {Number(line.amount || 0).toLocaleString("vi-VN")}
                    </td>
                  </tr>
                ))
              )}
              <tr className="bg-slate-50">
                <td
                  colSpan={2}
                  className="border border-slate-300 px-2 py-2 text-right font-bold uppercase text-[11px]"
                >
                  Tổng cộng
                </td>
                <td className="border border-slate-300 px-2 py-2 text-right font-black text-red-700 tabular-nums">
                  {totalAmount.toLocaleString("vi-VN")}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-[12px] text-slate-700 leading-relaxed">
            <span className="font-semibold">Số tiền bằng chữ:</span>{" "}
            <span className="italic">
              {formatAmountInWords(totalAmount) || "…………………………"}
            </span>
          </p>
        </section>

        <section className="grid grid-cols-2 gap-4 pt-2 text-[12px]">
          <div>
            <p className="font-bold text-slate-700 mb-1">Trạng thái phiếu</p>
            <p className="text-slate-600">{status}</p>
          </div>
          <div className="text-right">
            <p className="font-bold text-slate-700 mb-1">Ngày lập</p>
            <p className="text-slate-600">
              {formatDateVN(data?.created_at || data?.payment_date)}
            </p>
          </div>
        </section>

        <section className="pt-4 border-t border-slate-200">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-4 text-center">
            Chữ ký số
          </p>
          <div
            className={`grid gap-4 ${
              signatureSlots.length <= 4
                ? "grid-cols-2 sm:grid-cols-4"
                : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
            }`}
          >
            {signatureSlots.map((slot) => (
              <SignatureBlock key={slot.key} slot={slot} />
            ))}
          </div>
        </section>
      </div>
    </article>
  );
};

export default PaymentDocumentPaper;
